import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import Badge from '../../../components/bootstrap/Badge';
import Button from '../../../components/bootstrap/Button';
import Card, {CardActions, CardBody, CardHeader, CardLabel, CardTitle} from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import useRequest from '../../../hooks/useRequest';
import {adminsMenu} from '../../../menu';
import {useMst} from '../../../models';
import CompanyService from '../../../services/CompanyService';
import showNotification from '../../../components/extras/showNotification';
import {getBadgeInfo} from "../../../lib/Util";
import {getLocalizedField, RANK_NAME_OPTIONS} from '../../../helpers/localizedField';
import {getPersonDisplayName, pickPersonNames} from '../../../helpers/personNameI18n';

const ApprovalGraph = ({ cardClassName = '' }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const tr = useCallback((name) => t(`userDashboard.${name}`), [t]);
	const { company } = useMst();
	const { responseData, loading, request } = useRequest([]);
	const [loading2, setLoading2] = useState(false);
	const [originApprovalList, setOriginApprovalList] = useState([]);
	const [isFiltered, setIsFiltered] = useState(false);
	const [approvalList, setApprovalList] = useState([]);
	const [approvalCount, setApprovalCount] = useState({
		rest : 0,
		over : 0,
		work : 0
	});

	const [graphValues, setGraphValues] = useState({
		series: [
			{
				name: tr('numberApprovalRequest'),
				data: [],
			},
		],
		options: {
			chart: {
				type: 'bar',
				height: 350,
				toolbar: {
					download: false,
					show: false,
				},
			},
			plotOptions: {
				bar: {
					horizontal: true,
					barHeight: '60%',
				},
			},
			dataLabels: {
				enabled: true,
				offsetX: -6,
				style: {
					fontSize: '12px',
					colors: ['#fff'],
				},
			},
			xaxis: {
				categories: [],
				labels: {
					formatter: function (val) {
						return val.toFixed(0);
					},
				},
			},
		},
	});


	useEffect(()=> {
		let rest = approvalList?.filter(i => i.type?.includes('REST'));
		let over = approvalList?.filter(i => i.subtypeCd === 2); //
		let work = approvalList?.filter(i => i.subtypeCd !== 2 && i.type.includes('WORK'));
					
		setApprovalCount({
			rest : rest?.length ||0,
			over : over?.length || 0,
			work : work?.length || 0
		})
	}, [approvalList])

	useEffect(() => {
		request(
			CompanyService.getAdminRequestList,
			[company.get.id],
			(res) => (res?.data ? { data: res.data.filter((data) => data.state === 'REQUEST') } : { data: [] }),
			(error) => {
				showNotification(tr('approvalStatus'), tr('problemLoadingApprovalStatus'), 'danger');
			}
		);
	}, [company, request, t]);

	useEffect(() => {
		setLoading2(true);
 
		if (responseData.length > 0) {
			const sortedByCreatedAt = responseData.sort((a, b) => {
				if (a.createdAt === null || b.createdAt === null) {
					return;
				} else {
					return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
				}
			});
			setOriginApprovalList(sortedByCreatedAt);
			setApprovalList(sortedByCreatedAt);
			const approvalArray = sortedByCreatedAt.reduce((allApp, curApp) => {
				if (curApp.state === 'REQUEST') {
					if (curApp.restday?.name) {
						const alreadyExist = allApp.findIndex((app) => app.key === curApp.restday?.name);
						if (alreadyExist !== -1) {
							allApp[alreadyExist].value = allApp[alreadyExist].value + 1;
						} else {
							allApp.push({ key: curApp.restday?.name, value: 1 });
						}
					}
					return allApp;
				}
			}, []);
			const sortedByRequestCount = approvalArray.sort((a, b) => (a.value < b.value ? 1 : a.value > b.value ? -1 : 0));
			graphValues.options.chart.events = {
				dataPointMouseEnter: function (event) {
					event.target.style.cursor = 'pointer';
				},
				dataPointSelection: (event, chartContext, config) => {
					setApprovalList(
						sortedByCreatedAt.filter(
							(approval) =>
								approval.state === 'REQUEST' &&
								approval.subtype === graphValues.options.xaxis.categories[config.dataPointIndex]
						)
					);
					setIsFiltered(true);
				},
			};
			let graphValuesRef = { ...graphValues };
			graphValuesRef.series[0].data = sortedByRequestCount.map((req) => req.value);
			graphValuesRef.options.xaxis.categories = sortedByRequestCount.map((req) => req.key);
 
			graphValues.options.chart.height = 70 + sortedByRequestCount.length * 20;
			setGraphValues(graphValuesRef);
		}
		setLoading2(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [responseData]);

	return (
		<Card className={`custom-box-shadow rounded-2 approval_pending_card ${cardClassName}` }>
			<CardHeader className='rounded-2'>
				<CardLabel>
					<CardTitle
						className={'cursor-pointer'}
						onClick={() => {
							navigate(adminsMenu.approvalDocument.path);
						}}>
						{tr('approvalWaitingList')}
					</CardTitle>
				</CardLabel>
				<CardActions>
					<Badge color='light' className='text-wrap'>
						<span className='text-dark'>{tr('totalWaitingCnt')} : </span>
						<span className='fw-bold text-info fs-6'>{approvalList?.length || 0}</span>
					</Badge>
					{isFiltered ? (
						<Button
							color='primary'
							size='sm'
							isOutline
							onClick={() => {
								setApprovalList(originApprovalList);
								setIsFiltered(false);
							}}>
							{tr('viewAll')}
						</Button>
					) : (
						<></>
					)}
				</CardActions>
			</CardHeader>
			<CardBody>
				{(loading || loading2) && (
					<div className='h-100 d-flex flex-column justify-content-center align-items-center'>
						<p>{t('userDashboard.dataLoading')}</p>
					</div>
				)}
				{!loading && !loading2 && (
					graphValues.series[0].data.length > 0 &&
					graphValues.options.xaxis.categories.length > 0 &&
					approvalList.length > 0 ? (
						<div className='list_box'>
							<div className='overflow-auto mb-2'>
								<div className='d-flex py-4 bg-l10-info rounded-1'>
									<div className='d-flex flex-column justify-content-around col-4'>
										<div className='d-flex justify-content-center'>
											<span className=''>{t('approval.vacation')}</span>
										</div>
										<div className='d-flex justify-content-center mt-3 fw-bold fs-5 text-muted'>
											<span className='text-info me-1'>{approvalCount.rest || 0}</span>{t('common.label.item')}
										</div>
									</div>
									<div
										className='d-flex flex-column justify-content-around col-4'
										style={{
											borderLeft: '1px solid #D9D9D9',
											borderRight: '1px solid #D9D9D9',
										}}>
										<div className='d-flex justify-content-center'>
											<span className=''>{t('approval.overtime')}</span>
										</div>
										<div className='d-flex justify-content-center mt-3 fw-bold fs-5 text-muted'>
											<span className='text-info me-1'>{approvalCount.over || 0}</span>{t('common.label.item')}
										</div>
									</div>
									<div className='d-flex flex-column justify-content-around col-4'>
										<div className='d-flex justify-content-center'>
											<span className=''>{t('approval.working')}</span>
										</div>
										<div className='d-flex justify-content-center mt-3 fw-bold fs-5 text-muted'>
										<span className='text-info me-1'>{approvalCount.work || 0}</span>{t('common.label.item')}
										</div>
									</div>
								</div>
							</div>
							<div className='dashboard-approval-table overflow-scroll'
									 style={{height:'calc(14vh + 55px)', flex:'auto', overscrollBehavior: 'contain'}}>
								<table className='w-100' style={{tableLayout: 'fixed'}}>
									<colgroup>
										<col width='*'/>
										<col width='100px'/>
										<col width='*'/>
									</colgroup>
									{
										approvalList.map((v, i) => {
											let data = getBadgeInfo(v, t);
											return (
												<tr
													key={i}
													className='border-bottom border-light'
													style={{cursor:'pointer'}}
													onClick={() => {
														navigate(`/${adminsMenu.approvalDocument.path}`, {
															state: {
																id: approvalList[i]?.id,
																startDate: moment(v?.startDate).format('YYYY-MM-DD'),
																endDate: moment(v?.endDate).format('YYYY-MM-DD'),
																type: 'approvalReportAll'
															},
														});
													}}
												>
													<td className='py-3'>
														<p className='mb-0 fw-bold' 
														style={{
															minWidth: '40px',
															overflow: 'hidden',
  															whiteSpace: 'nowrap',
  															textOverflow: 'ellipsis',
  															wordBreak: 'break-all'
														}}>
															{getPersonDisplayName(pickPersonNames(v, 'user'), undefined, v.user)} {getLocalizedField(v, 'rank', undefined, RANK_NAME_OPTIONS)}
														</p>
													</td>
													<td className='py-3'>
														<p className='mb-0 text-muted' style={{minWidth: '110px'}}>
															<Icon icon='CalendarToday' className='me-2' />
															<span>{moment(v.createdAt).format('YYYY-MM-DD')}</span>
														</p>
													</td>
													<td className='py-3' style={{minWidth: '60px'}}>
														<Badge className={`px-2`} color={data?.bg} style={{backgroundColor: data?.bg}}>
															<span
																className='fs-6'
																style={{color: data?.fc}}
															>
																{t(data?.name)}
															</span>
														</Badge>
													</td>
												</tr>
											)
										})
									}
								</table>
							</div>
						</div>
					) : (
						<div className='h-100 d-flex flex-column justify-content-center align-items-center text-muted'>
							<Icon size={'6x'} icon={'CheckCircle'} />
							<p className='mt-5'>{tr('noPendingApproval')}</p>
						</div>
					))}
			</CardBody>
		</Card>
	);
};

export default ApprovalGraph;
